// Consumes a pending native-to-existing-route handoff (e.g. the native
// Notification Center opening a message notification hands off to the
// existing /chat/{chatId} route) whenever MainActivity's WebView is next
// in the foreground. See android/.../notifications/NativeNotificationCenterPlugin.java
// for why this is a static-field handoff rather than an Intent extra.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  addPendingHandoffChangeListener,
  consumeNativeNotificationHandoff,
  isNativeNotificationCenterAvailable,
} from "../plugins/NativeNotificationCenter";
import { addNativeAppForegroundChangeListener } from "../plugins/NativeAppForeground";

export default function NativeHandoffListener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeNotificationCenterAvailable()) {
      return;
    }

    const checkPendingHandoff = async () => {
      const path = await consumeNativeNotificationHandoff();
      if (path) {
        navigate(path);
      }
    };

    void checkPendingHandoff();

    const foregroundListenerHandle = addNativeAppForegroundChangeListener((isForeground) => {
      if (isForeground) {
        void checkPendingHandoff();
      }
    });

    // Covers the case the foreground listener alone misses: opening a
    // notification from within an already-running app hands off from
    // NotificationCenterActivity straight to the already-resumed
    // MainActivity (no app-level background/foreground edge ever fires -
    // see NativeNotificationCenterPlugin.setPendingHandoffPath). This event
    // is a timeliness nudge only; the route itself is still fetched
    // through consumeNativeNotificationHandoff() above.
    const pendingHandoffListenerHandle = addPendingHandoffChangeListener(() => {
      void checkPendingHandoff();
    });

    return () => {
      void foregroundListenerHandle?.remove?.();
      void pendingHandoffListenerHandle?.remove?.();
    };
  }, [navigate]);

  return null;
}
